import { Injectable, NgZone, OnDestroy } from '@angular/core';

/**
 * Keeps `--app-vh` in sync with the height the browser is *actually* giving
 * the page.
 *
 * Everything that has to be exactly one screen tall — the app shell, the login
 * background, the display board, modal max-heights — sizes off `--app-vh`,
 * which styles.css seeds with `100dvh`. That is the right answer *when the
 * browser re-resolves it*, and iOS Safari frequently doesn't: after a reload, a
 * bfcache restore, or the PWA being resumed from the background, the dynamic
 * viewport can stay pinned to whatever it was at first layout. The shell then
 * comes out shorter than the screen and leaves a blank strip under the content
 * with the list clipped above it, until some gesture that forces a full
 * re-layout (a pull-to-refresh drag) shakes it loose.
 *
 * So we measure it ourselves and write a pixel value over the seed. The
 * listeners cover every moment iOS changes its mind: visualViewport
 * resize/scroll (URL bar collapsing/expanding), orientationchange, pageshow
 * (bfcache restore), and visibilitychange (PWA resumed). Each of those also
 * kicks off a short settle sequence, because iOS reports the *previous*
 * metrics for a few frames after a rotation or a restore.
 *
 * In a browser tab we read `window.innerHeight`, not `visualViewport.height`,
 * on purpose: the visual viewport shrinks when the on-screen keyboard opens or
 * the user pinch-zooms, and collapsing the whole shell in either case would be
 * a worse bug than the one this fixes. Installed on the home screen the rules
 * differ — see readViewportHeight().
 *
 * Because the shell deliberately does NOT shrink for the keyboard, the
 * keyboard just covers the bottom of it — and a field near the bottom of a
 * page or modal (a chat composer, the last field of a form) has no content
 * below it to scroll up into, so it stays hidden while the user types. The
 * same listeners therefore also publish `--kb-inset`: how much of the shell
 * the keyboard is covering, in px (0 when closed). styles.css adds that as
 * extra scroll room at the bottom of the page scroller and of modals, and
 * revealFocused() scrolls the focused field up above the keyboard.
 */
@Injectable({ providedIn: 'root' })
export class ViewportHeightService implements OnDestroy {
  /** ms after a viewport-changing event at which iOS has usually settled. */
  private readonly SETTLE_MS = [120, 400, 1000];

  /** Launching a home-screen PWA settles later, and fires no resize when it does. */
  private readonly SETTLE_MS_STANDALONE = [120, 400, 1000, 2000, 3500];

  /** Below this the "keyboard" is really the URL bar or a rounding gap. */
  private readonly MIN_KEYBOARD_PX = 80;

  /** Space kept between the focused field and the top of the keyboard. */
  private readonly KEYBOARD_GAP_PX = 16;

  private lastApplied = -1;
  private lastInset = -1;
  private timers: number[] = [];
  private started = false;

  private readonly onViewportChange = (): void => {
    this.measure();
    this.measureKeyboard();
  };

  /** A field just took focus: the keyboard is (about to be) up. Reveal it
   *  now and again once the keyboard animation has settled. */
  private readonly onFocusIn = (): void => {
    if (!this.isEditableFocused()) return;
    this.measureKeyboard();
    for (const ms of [100, 350, 700]) window.setTimeout(() => this.measureKeyboard(true), ms);
  };

  private readonly onFocusOut = (): void => {
    // Let focus settle — tapping from one field to the next fires focusout
    // then focusin, and the keyboard never actually closes.
    window.setTimeout(() => this.measureKeyboard(), 50);
  };

  /** A growing textarea (or typing past the end of a field) can push the
   *  caret back under the keyboard. */
  private readonly onInput = (): void => {
    if (this.lastInset > 0) this.revealFocused();
  };

  private readonly onSettleEvent = (): void => {
    this.measure();
    this.settle();
  };

  private readonly onVisibilityChange = (): void => {
    // Measuring a hidden document reads whatever it had before it was
    // backgrounded; only the return trip is worth acting on.
    if (document.visibilityState === 'visible') this.onSettleEvent();
  };

  constructor(private zone: NgZone) {}

  init(): void {
    if (this.started) return;
    this.started = true;

    // Pure DOM bookkeeping — never worth a change-detection pass, and
    // visualViewport 'scroll' fires on every frame of a URL-bar animation.
    this.zone.runOutsideAngular(() => {
      const vv = window.visualViewport;
      vv?.addEventListener('resize', this.onViewportChange);
      vv?.addEventListener('scroll', this.onViewportChange);
      window.addEventListener('resize', this.onViewportChange);
      window.addEventListener('orientationchange', this.onSettleEvent);
      window.addEventListener('pageshow', this.onSettleEvent);
      document.addEventListener('visibilitychange', this.onVisibilityChange);
      document.addEventListener('focusin', this.onFocusIn);
      document.addEventListener('focusout', this.onFocusOut);
      document.addEventListener('input', this.onInput);
    });

    this.onSettleEvent();
  }

  ngOnDestroy(): void {
    const vv = window.visualViewport;
    vv?.removeEventListener('resize', this.onViewportChange);
    vv?.removeEventListener('scroll', this.onViewportChange);
    window.removeEventListener('resize', this.onViewportChange);
    window.removeEventListener('orientationchange', this.onSettleEvent);
    window.removeEventListener('pageshow', this.onSettleEvent);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    document.removeEventListener('focusin', this.onFocusIn);
    document.removeEventListener('focusout', this.onFocusOut);
    document.removeEventListener('input', this.onInput);
    this.clearTimers();
  }

  // ── Keyboard inset ────────────────────────────────────────────────────────

  /** Publish how much of the shell the on-screen keyboard covers, and (on
   *  `reveal`, or whenever it changes) scroll the focused field above it. */
  private measureKeyboard(reveal = false): void {
    const inset = this.readKeyboardInset();
    if (inset !== this.lastInset) {
      this.lastInset = inset;
      const root = document.documentElement;
      root.style.setProperty('--kb-inset', `${inset}px`);
      root.classList.toggle('keyboard-open', inset > 0);
      reveal = true;
    }
    // After the new padding has been laid out, so there's room to scroll into.
    if (reveal && inset > 0) requestAnimationFrame(() => this.revealFocused());
  }

  /** Bottom of the shell hidden behind the keyboard, in layout px. The shell
   *  is the larger of the current window and the height we last applied —
   *  an installed PWA shrinks innerHeight for the keyboard while --app-vh
   *  stays put (see measure()). The visible strip is the visual viewport,
   *  which iOS may also have panned down (offsetTop). */
  private readKeyboardInset(): number {
    const vv = window.visualViewport;
    if (!vv || vv.scale < 0.99 || !this.isEditableFocused()) return 0;
    const shell = Math.max(window.innerHeight, this.lastApplied);
    const hidden = Math.round(shell - (vv.offsetTop + vv.height));
    return hidden >= this.MIN_KEYBOARD_PX ? hidden : 0;
  }

  /** Scroll the focused field's nearest scrolling container just enough that
   *  the field sits above the keyboard. Doesn't use scrollIntoView(): that
   *  aims for the layout viewport, which the keyboard overlaps. */
  private revealFocused(): void {
    const el = document.activeElement as HTMLElement | null;
    const vv = window.visualViewport;
    if (!el || !vv || !this.isEditableFocused()) return;

    const visibleBottom = vv.offsetTop + vv.height - this.KEYBOARD_GAP_PX;
    const rect = el.getBoundingClientRect();
    // A field taller than the visible strip (a big textarea): keep the caret
    // end in view rather than the top edge.
    const overflow = rect.bottom - visibleBottom;
    if (overflow <= 4) return;

    const scroller = this.scrollParent(el);
    if (scroller) scroller.scrollTop += overflow;
  }

  /** The page scroller or a modal for screens inside the shell; the document
   *  itself for ones outside it (the login page). */
  private scrollParent(el: HTMLElement): HTMLElement | null {
    for (let p = el.parentElement; p; p = p.parentElement) {
      const { overflowY } = getComputedStyle(p);
      if ((overflowY === 'auto' || overflowY === 'scroll') && p.scrollHeight > p.clientHeight) return p;
    }
    const doc = document.scrollingElement as HTMLElement | null;
    return doc && doc.scrollHeight > doc.clientHeight ? doc : null;
  }

  private measure(): void {
    // Standalone PWAs shrink window.innerHeight itself when the on-screen
    // keyboard opens (a Safari tab doesn't — see readViewportHeight() below).
    // Applying that shrunk height to --app-vh collapses the app shell and,
    // with it, any open modal (whose backdrop is sized off --app-vh), tearing
    // the dark overlay away from the bottom of the real screen and exposing
    // whatever's fixed-positioned on the page underneath. Skip the update
    // while a field is focused so a keyboard opening can't shrink anything;
    // the next blur-triggered resize measures the real height again.
    if (this.isStandalone() && this.isEditableFocused()) return;

    const h = this.readViewportHeight();
    if (h <= 0 || h === this.lastApplied) return;
    this.lastApplied = h;
    document.documentElement.style.setProperty('--app-vh', `${h}px`);
  }

  private isEditableFocused(): boolean {
    const el = document.activeElement as HTMLElement | null;
    if (!el) return false;
    return el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT' || el.isContentEditable;
  }

  private readViewportHeight(): number {
    // In a browser tab window.innerHeight is the measurement that tracks the
    // URL bar collapsing, and it's the one that fixed this in Safari. Don't
    // touch it.
    if (!this.isStandalone()) return Math.round(window.innerHeight);

    // Installed on the home screen there is no browser chrome at all, so every
    // honest measurement of the window should agree and the largest one wins.
    // It matters because iOS hands out a short window.innerHeight for the
    // first frames after launching a `black-translucent` PWA — the web view is
    // already full screen while innerHeight still excludes the status bar,
    // which is precisely the missing strip at the bottom. visualViewport can
    // only ever shrink (keyboard, pinch-zoom), so taking the larger of the two
    // never lets either of those collapse the shell — except when the user has
    // pinch-zoomed *out*, where it legitimately overshoots the layout
    // viewport, hence the scale guard.
    const vv = window.visualViewport;
    let h = window.innerHeight;
    if (vv && vv.scale >= 0.99) h = Math.max(h, vv.height);
    return Math.round(h);
  }

  /** Launched from the home screen rather than opened in a browser tab. */
  private isStandalone(): boolean {
    return window.matchMedia?.('(display-mode: standalone)').matches === true
      || (navigator as Navigator & { standalone?: boolean }).standalone === true;
  }

  /** Re-check for the next few seconds — iOS settles its metrics lazily. */
  private settle(): void {
    this.clearTimers();
    const schedule = this.isStandalone() ? this.SETTLE_MS_STANDALONE : this.SETTLE_MS;
    this.zone.runOutsideAngular(() => {
      requestAnimationFrame(() => this.measure());
      for (const ms of schedule) {
        this.timers.push(window.setTimeout(() => this.measure(), ms));
      }
    });
  }

  private clearTimers(): void {
    for (const t of this.timers) window.clearTimeout(t);
    this.timers = [];
  }
}
